import {useLazyQuery} from '@apollo/client';
import React, {useEffect, useState} from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import {Search} from '@mui/icons-material';
import {Box} from '@mui/system';
import {CircularProgress, InputAdornment, TextField} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {GET_ACCOUNTS} from '../AccountOverview/accountOverviewQueries';
import {accounts, accountsVariables} from '../../schema-types/accounts';
import {AccountSearchFragment} from '../../schema-types/AccountSearchFragment';
import {useDebounce} from '../../hooks/useDebounce';
import {themeColors} from '../../theme/Theme';

export const HeaderSearch: React.FC<{}> = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState<string>('');
  const [value, setValue] = useState<AccountSearchFragment | null>(null);
  const delayedSearch = useDebounce(search, 1000);
  const [
    getAccountsOnly,
    {loading: accountsLoading, error: accountsError, data: accountsData},
  ] = useLazyQuery<accounts, accountsVariables>(GET_ACCOUNTS);
  useEffect(() => {
    if (value?.name === search) {
      return;
    }
    if (!search) {
      return;
    }
    getAccountsOnly({
      variables: {
        // search: 'regression', // FOR TESTING/EDITING
        search: delayedSearch.length < 3 ? null : delayedSearch,
      },
    });
  }, [delayedSearch, value]);

  return (
    <>
      <Autocomplete
        value={null}
        filterOptions={(x) => x}
        inputValue={search}
        onInputChange={(event, newInputValue) => {
          setSearch(newInputValue);
        }}
        includeInputInList
        onChange={(event, newValue, reason) => {
          if (
            event.type === 'keydown' &&
            ((event as React.KeyboardEvent).key === 'Backspace' ||
              (event as React.KeyboardEvent).key === 'Delete') &&
            reason === 'removeOption'
          ) {
            return;
          }
          if (newValue) {
            setValue(newValue);
            navigate({pathname: `/account/${newValue.id}`});
          }
        }}
        noOptionsText="Search all accounts here"
        disableCloseOnSelect
        renderTags={() => null}
        renderOption={(props, option, {selected}, {onClose}) => {
          const {key, ...optionProps} = props;
          const hasBusinessUnit = !!option.businessUnit?.name;
          const nameParts = option.name?.includes(',')
            ? option.name?.split(',')
            : option.name;

          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
            <li
              key={key}
              onClick={(e) => {
                navigate({pathname: `/account/${option.id}`});
                onClose?.(e, 'escape');
              }}
              {...optionProps}
              style={{display: 'block'}}
            >
              <Box
                fontWeight="bold"
                sx={(t) => ({
                  color: hasBusinessUnit ? themeColors.backgroundDark : themeColors.grey,
                })}
              >
                {typeof nameParts === 'string' || nameParts === null ? (
                  nameParts
                ) : (
                  <span>
                    {nameParts[0]},&nbsp;
                    <span style={{display: 'inline-block'}}>{nameParts[1]}</span>
                  </span>
                )}
                <br />
              </Box>
              {hasBusinessUnit ? (
                <Box color={themeColors.backgroundMuted}>{option.businessUnit?.name}</Box>
              ) : (
                <Box color={themeColors.grey}>No business unit</Box>
              )}
            </li>
          );
        }}
        renderInput={({...params}) => (
          <TextField
            ref={params.InputProps.ref}
            {...params}
            variant="outlined"
            sx={{width: 400, border: 0, p: 0.2}}
            placeholder="Search accounts"
            slotProps={{
              input: {
                sx: {
                  bgcolor: 'white',
                  pt: '5px !important',
                  pb: '5px !important',
                },
                startAdornment: (
                  <InputAdornment position="start" sx={{ml: 1}}>
                    {accountsLoading ? <CircularProgress size="20px" /> : <Search />}
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
        options={[
          ...(accountsData?.accounts?.filter((account) => account.businessUnit) || []),
          ...(accountsData?.accounts?.filter((account) => !account.businessUnit) || []),
        ]}
        getOptionLabel={(option) => option.name || ''}
        slotProps={{}}
      />
    </>
  );
};
